import { useCallback } from 'react';
import { errorCode } from '../lib/httpError';
import { isSignOutRejection } from '../lib/signOuts';
import { recordShownError } from '../observability';
import { useI18n } from '../i18n';
import { pushSupported, usePushStore, type PushEnableResult, type PushDisableResult, type PushPermission } from '../store/push';
import { useToast } from './useToast';

export type { PushEnableResult, PushDisableResult } from '../store/push';

// The selector over the push state of this device. The state, the lock, the
// subscribe sequence and the read of the device are in `store/push.ts` (#664,
// F37): the bell, the opt-in banner and the push-off line all read the same
// state, and a change through one reaches the others. This file keeps the
// return type of the hook and the two message functions below.
//
// `supported` is false on a browser with no service worker or no PushManager
// (Safari before 16.4, most in-app browsers). `permission` mirrors
// Notification.permission: a 'denied' device never gets a prompt again.

export interface PushSubscriptionState {
  supported: boolean;
  enabled: boolean;
  /** #557 — the store reads the subscription of this device. Before that,
   *  `enabled` is false on every device, so an offer of push (the banner, the
   *  push-off line) waits for this flag. */
  checked: boolean;
  loading: boolean;
  permission: PushPermission;
  enable: () => Promise<boolean>;
  /** #557 — `enable()` with the reason of a failure. Give the result to
   *  `usePushEnableMessage()`, so that a failed Enable shows a message. */
  enableWithResult: () => Promise<PushEnableResult>;
  /** True when this device has no subscription after the call. */
  disable: () => Promise<boolean>;
  /** #557 review — `disable()` with the reason of a failure. Give the result
   *  to `usePushDisableMessage()`, so that a failed Disable shows a message. */
  disableWithResult: () => Promise<PushDisableResult>;
}

// The actions are the same function for every render and every caller.
const enableWithResult = (): Promise<PushEnableResult> => usePushStore.getState().enable();
const disableWithResult = (): Promise<PushDisableResult> => usePushStore.getState().disable();
const enable = async (): Promise<boolean> => (await enableWithResult()).ok;
const disable = async (): Promise<boolean> => (await disableWithResult()).ok;

export function usePushSubscription(): PushSubscriptionState {
  const enabled = usePushStore(s => s.enabled);
  const checked = usePushStore(s => s.checked);
  const loading = usePushStore(s => s.loading);
  const permission = usePushStore(s => s.permission);
  return { supported: pushSupported, enabled, checked, loading, permission, enable, enableWithResult, disable, disableWithResult };
}

type ShowToast = ReturnType<typeof useToast>['showToast'];
type ShowError = ReturnType<typeof useToast>['showError'];

/** #557 — the error toast of a failed push action. An answer with a `code`
 *  goes to `showError()`, which translates the code and records
 *  `ui_error_shown`. Any other failure shows `fallback`, and an HTTP error is
 *  recorded as `ui_error_shown` too. #666 — A sign-out ended the request: no
 *  toast and no record. */
function showPushFailure(err: unknown, fallback: string, showToast: ShowToast, showError: ShowError): void {
  if (isSignOutRejection(err)) return;
  if (errorCode(err)) { showError(err); return; }
  recordShownError(err, 'toast');
  showToast(fallback, 'error');
}

/**
 * #557 — the message of a failed Enable, as an error toast. The bell, the
 * opt-in banner and the push-off line give their result to this function.
 * Before, `enable()` hid every failure, and a tap on "Enable" showed nothing
 * when GET /vapid-key or POST /push-subscribe failed.
 *   - The browser blocks push for this site: `pushBlocked`.
 *   - An answer with a `code` (PUSH_TOO_MANY_SUBSCRIPTIONS, …): `showError()`
 *     translates the code and records `ui_error_shown`.
 *   - Any other failure (a 500, the network, the service worker, the push
 *     service, a server with no VAPID key): `pushEnableFailed`. An HTTP error
 *     is recorded as `ui_error_shown` too.
 *   - A busy result (#664, the lock dropped the call): nothing.
 * Call it inside <ToastProvider> (useToast() throws outside it).
 */
export function usePushEnableMessage(): (result: PushEnableResult) => void {
  const { t } = useI18n();
  const { showToast, showError } = useToast();
  return useCallback((result: PushEnableResult) => {
    if (result.ok || result.busy) return;
    if (result.denied) { showToast(t.notifications.pushBlocked, 'error'); return; }
    showPushFailure(result.error, t.notifications.pushEnableFailed, showToast, showError);
  }, [t, showToast, showError]);
}

/**
 * #557 review — the message of a failed Disable, as an error toast. The push
 * toggle of the bell gives its result to this function. Before, `disable()`
 * hid every failure, and the toggle stayed on with no message.
 *   - An answer with a `code`: `showError()` translates the code.
 *   - Any other failure (a 500, the network, the service worker, the push
 *     service): `pushDisableFailed`.
 *   - A busy result (#664, the lock dropped the call): nothing.
 * A sign-out never calls it: logout() releases the subscription silently.
 * Call it inside <ToastProvider> (useToast() throws outside it).
 */
export function usePushDisableMessage(): (result: PushDisableResult) => void {
  const { t } = useI18n();
  const { showToast, showError } = useToast();
  return useCallback((result: PushDisableResult) => {
    if (result.ok || result.busy) return;
    showPushFailure(result.error, t.notifications.pushDisableFailed, showToast, showError);
  }, [t, showToast, showError]);
}
